import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, MessageSquare } from "lucide-react"

export default function Home() {
  return (
    
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-800 to-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Schedule Events with AI Simplicity</h1>
            <p className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto text-slate-300">
              Create Google Calendar events using natural language. Just describe your meeting, and we'll handle the
              rest—saving you multiple clicks and making scheduling effortless!
            </p>
            <Link
              to="/create-event"
              className="inline-flex items-center gap-2 bg-slate-100 text-slate-900 px-6 py-3 rounded-md font-medium hover:bg-white transition-colors"
            >
              Get Started <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">How It Works</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our AI-powered assistant makes calendar management effortless
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="bg-slate-50 p-8 rounded-lg text-center">
              <div className="bg-slate-200 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <MessageSquare className="text-slate-800" size={28} />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-slate-900">Describe Your Event</h3>
              <p className="text-slate-600">
                Type or speak your event details in natural language. For example, "Schedule a team meeting tomorrow at 2pm for 1
                hour with Jon Doe (JonDoe@gmail.com)"
              </p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg text-center">
              <div className="bg-slate-200 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="text-slate-800" size={28} />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-slate-900">AI Processes Details</h3>
              <p className="text-slate-600">
                {/* Our AI understands dates, times, urls (zoom, meets...) and context to create the perfect calendar event with all necessary
                details. */}
                Our AI takes care of the rest: 
                add everything to Google Calendar (summary, description, attendees...), insert links from Zoom, Microsoft Teams or any other platform. 
                Need a Google Meet link? Just ask and we'll create it for you automatically!
              </p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg text-center">
              <div className="bg-slate-200 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Calendar className="text-slate-800" size={28} />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-slate-900">Event Created</h3>
              <p className="text-slate-600">
                The event is automatically added to your Google Calendar with all the right details, ready to go.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How to Use Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:gap-12">
            <div className="md:w-1/2 mb-10 md:mb-0">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">Getting Started is Easy</h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-slate-200 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-bold text-slate-800">1</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-slate-900">Sign in with Google</h3>
                    <p className="text-slate-600">
                      Connect your Google account to give our app permission to create calendar events.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-slate-200 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-bold text-slate-800">2</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-slate-900">Describe Your Event</h3>
                    <p className="text-slate-600">
                      Say what you want to schedule using everyday language. Include date, time, and any other details.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-slate-200 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-bold text-slate-800">3</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-slate-900">Click Create Event</h3>
                    <p className="text-slate-600">
                      Our AI will process your request and create the event in your Google Calendar instantly.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:w-1/2 bg-white p-6 rounded-lg shadow-lg">
              <div className="border border-slate-200 rounded-lg p-4 mb-4">
                <div className="text-sm text-slate-500 mb-2">Example Input:</div>
                <div className="bg-slate-50 p-3 rounded">
                  "Schedule a product review meeting with the design team on April 2nd at 10am for 45 minutes. 
                  Add a note to prepare mockups."
                </div>
              </div>

              <div className="border border-slate-200 rounded-lg p-4">
                <div className="text-sm text-slate-500 mb-2">AI Creates:</div>
                <div className="bg-slate-50 p-3 rounded">
                  <div className="font-medium">Product Review with Design Team</div>
                  <div className="text-sm text-slate-600 mt-1">Tuesday, Apr 2 • 10:00 - 10:45 AM</div>
                  <div className="text-sm text-slate-600 mt-1">Note: Please prepare mockups</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-800 text-white text-center">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Simplify Your Calendar Management?</h2>
          <p className="text-xl text-slate-300 mb-10 max-w-3xl mx-auto">
            Join us and save time by letting AI handle your scheduling.
          </p>
          <Link
            to="/create-event"
            className="inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-md font-medium text-lg hover:bg-slate-100 transition-colors"
          >
            Try It Now <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-xl font-bold text-white mb-4">AI Google Calendar Assistant</div>
            <p className="mb-6">Simplifying scheduling with artificial intelligence</p>

            <div className="mb-4 space-x-4">
              <a href="/privacy-policy" className="hover:underline text-slate-400">
                Privacy Policy
              </a>
              <a href="/terms" className="hover:underline text-slate-400">
                Terms & Conditions
              </a>
            </div>

            <div className="text-sm">© {new Date().getFullYear()} AI Calendar Assistant. All rights reserved.</div>
          </div>
        </div>
      </footer>
    </div>
  )
}